import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Edit2, Trash2, RotateCcw, HeartPulse, Copy, Check, RefreshCw } from 'lucide-react';
import { vpnServersAPI } from '../api';
import { Card, Button, Badge, Table, Pagination, Modal, Input, Select, PageHeader, Alert, useSort } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const MenuItem = ({ children, onClick, color, loading }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', background: 'none', border: 'none',
    cursor: loading ? 'wait' : 'pointer', fontSize: 13,
    color: color || 'var(--text-secondary)', transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const DropdownPortal = ({ anchorRef, open, children }) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX });
    }
  }, [open, anchorRef]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: pos.top, left: pos.left, transform: 'translateX(-100%)', zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 170, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};

const HealthBadge = ({ status }) => {
  const map = {
    healthy:     { color: 'var(--accent-green)',  bg: 'rgba(16,185,129,0.12)' },
    unreachable: { color: 'var(--accent-red)',    bg: 'rgba(239,68,68,0.12)' },
    unknown:     { color: 'var(--text-muted)',    bg: 'var(--bg-input)' },
  };
  const s = map[status] || map.unknown;
  return <span style={{ fontSize: 11, padding: '2px 8px', background: s.bg, color: s.color, borderRadius: 20, border: `1px solid ${s.color}44` }}>{status || 'unknown'}</span>;
};

const EMPTY_FORM = {
  name: '', type: 'wireguard', description: '', host: '', agent_port: 8090, agent_token: '',
  endpoint: '', pool: '', allowed_ips: '0.0.0.0/0', dns: '1.1.1.1', keepalive: 25,
  server_pubkey: '', psk: '', status: 'active',
};

export default function VpnServers() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const [servers, setServers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);
  const [bulkChecking, setBulkChecking] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [checkingId, setCheckingId] = useState(null);
  const [fetchingPubkey, setFetchingPubkey] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);

  const { sort, onSortChange, sortParam } = useSort();

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { page, per_page: perPage };
      if (onlyDeleted) params.only_deleted = true;
      if (typeFilter) params.type = typeFilter;
      if (q) params.q = q;
      if (sortParam) params.sort = sortParam;
      const res = await vpnServersAPI.list(params);
      setServers(res.vpn_servers || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [page, perPage, onlyDeleted, typeFilter, q, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [onlyDeleted, typeFilter]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => { setEditTarget(null); setForm(EMPTY_FORM); setFormError(null); setModalOpen(true); };
  const openEdit = (s) => {
    setEditTarget(s);
    setForm({
      name: s.name || '', type: s.type, description: s.description || '',
      host: s.host || '', agent_port: s.agent_port ?? 8090, agent_token: '',
      endpoint: s.endpoint || '', pool: s.pool || '', allowed_ips: s.allowed_ips || '0.0.0.0/0',
      dns: s.dns || '', keepalive: s.keepalive ?? 25, server_pubkey: s.server_pubkey || '',
      psk: '', status: s.status || 'active',
    });
    setFormError(null); setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setFormError(null);
    try {
      const payload = { ...form };
      if (editTarget) {
        delete payload.type; // type gak bisa diubah lewat PATCH
        if (!payload.agent_token) delete payload.agent_token;
        if (!payload.psk) delete payload.psk;
        await vpnServersAPI.update(editTarget.id, payload);
      } else {
        if (payload.type !== 'ipsec') delete payload.psk;
        if (payload.type !== 'wireguard') { delete payload.dns; delete payload.keepalive; }
        delete payload.server_pubkey; // server_pubkey cuma diisi lewat edit ("Get Public Key")
        await vpnServersAPI.create(payload);
      }
      setModalOpen(false); setSuccess(editTarget ? 'VPN server diperbarui.' : 'VPN server ditambahkan.'); load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || deletePermanent);
    try {
      if (permanent) await vpnServersAPI.deletePermanent(deleteConfirm.id);
      else await vpnServersAPI.delete(deleteConfirm.id);
      setDeleteConfirm(null); setDeletePermanent(false);
      setSuccess(permanent ? 'VPN server dihapus permanen.' : 'VPN server dihapus.'); load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (s) => {
    try { await vpnServersAPI.restore(s.id); setSuccess('VPN server dipulihkan.'); load(); }
    catch (e) { setError(e.message); }
  };

  const handleHealthCheck = async (s) => {
    setCheckingId(s.id);
    try {
      const res = await vpnServersAPI.health(s.id);
      setServers(list => list.map(x => x.id === s.id ? {
        ...x, health_status: res.reachable ? 'healthy' : 'unreachable',
        health_latency_ms: res.latency_ms, health_error: res.error, health_checked_at: res.checked_at,
      } : x));
      setSuccess(res.reachable ? `${s.name}: reachable (${res.latency_ms ?? '?'}ms)` : `${s.name}: unreachable — ${res.error || ''}`);
    } catch (e) { setError(e.message); }
    finally { setCheckingId(null); }
  };

  const handleBulkDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || bulkDeletePermanent);
    try {
      if (permanent) await Promise.all(selected.map(id => vpnServersAPI.deletePermanent(id)));
      else await Promise.all(selected.map(id => vpnServersAPI.delete(id)));
      setSuccess(`${selected.length} VPN server ${permanent ? 'dihapus permanen' : 'dihapus'}.`);
      setSelected([]); setBulkDeleteConfirm(false); setBulkDeletePermanent(false); load();
    } catch (e) { setError(e.message); setBulkDeleteConfirm(false); }
  };

  const handleBulkRestore = async () => {
    try {
      await Promise.all(selected.map(id => vpnServersAPI.restore(id)));
      setSuccess(`${selected.length} VPN server dipulihkan.`); setSelected([]); load();
    } catch (e) { setError(e.message); }
  };

  const handleBulkHealthCheck = async () => {
    setBulkChecking(true);
    try {
      const results = await Promise.all(selected.map(id => vpnServersAPI.health(id).then(r => ({ id, r })).catch(() => null)));
      setServers(list => list.map(x => {
        const found = results.find(res => res && res.id === x.id);
        if (!found) return x;
        return { ...x, health_status: found.r.reachable ? 'healthy' : 'unreachable', health_latency_ms: found.r.latency_ms, health_error: found.r.error, health_checked_at: found.r.checked_at };
      }));
      setSuccess(`${selected.length} VPN server dicek health.`);
    } catch (e) { setError(e.message); }
    finally { setBulkChecking(false); }
  };

  const handleSyncPubkey = async () => {
    setFetchingPubkey(true); setFormError(null);
    try {
      const res = await vpnServersAPI.syncPubkey(editTarget.id);
      f('server_pubkey', res.server_pubkey || '');
      setSuccess('Public key berhasil diambil dari agent.');
      load();
    } catch (e) { setFormError(e.message); }
    finally { setFetchingPubkey(false); }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true, render: (v, s) => (
        <div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--text-mono)' }}>{v}</div>
          {s.description && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.description}</div>}
        </div>
      ) },
    { key: 'type', label: 'Type', sortable: true, render: v => <span style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-input)', borderRadius: 4, border: '1px solid var(--border)', textTransform: 'uppercase' }}>{v}</span> },
    { key: 'host', label: 'Agent Host', sortable: true, render: (v, s) => <span className="mono" style={{ fontSize: 12 }}>{v}:{s.agent_port}</span> },
    { key: 'endpoint', label: 'Endpoint (public)', render: v => <span className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v}</span> },
    { key: 'pool', label: 'Pool', render: v => <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v}</span> },
    { key: 'status', label: 'Status', sortable: true, render: v => <Badge status={v} /> },
    { key: 'health_status', label: 'Health', render: (v, s) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <HealthBadge status={v} />
          <button onClick={() => handleHealthCheck(s)} disabled={checkingId === s.id}
            style={{ background: 'none', border: 'none', cursor: checkingId === s.id ? 'wait' : 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 2 }}
            title="Cek health sekarang">
            <HeartPulse size={13} />
          </button>
        </div>
      ) },
    {
      key: 'id', label: '', render: (_, s) => {
        const btnRef = React.createRef();
        return (
          <div>
            <button ref={btnRef} onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === s.id ? null : s.id); }}
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>···</button>
            <DropdownPortal anchorRef={btnRef} open={openMenuId === s.id}>
              {onlyDeleted ? (
                isSuperadmin && <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(s); setOpenMenuId(null); }}><RotateCcw size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Restore</MenuItem>
              ) : (<>
                {isSuperadmin && <MenuItem onClick={() => { openEdit(s); setOpenMenuId(null); }}><Edit2 size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Edit</MenuItem>}
                {isSuperadmin && <div style={{ borderTop: '1px solid var(--border)' }} />}
                {isSuperadmin && <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(s); setDeletePermanent(false); setOpenMenuId(null); }}><Trash2 size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Hapus</MenuItem>}
              </>)}
            </DropdownPortal>
          </div>
        );
      }
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="VPN — Instance Server"
        subtitle={`${total} instance${onlyDeleted ? ' (terhapus)' : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selected.length > 0 && (<>
              {!onlyDeleted && <Button variant="ghost" loading={bulkChecking} onClick={handleBulkHealthCheck}><HeartPulse size={13} /> Cek Health {selected.length}</Button>}
              {onlyDeleted && isSuperadmin && <Button variant="success" onClick={handleBulkRestore}><RotateCcw size={13} /> Restore {selected.length}</Button>}
              {isSuperadmin && (
                <Button variant="danger" onClick={() => setBulkDeleteConfirm(true)}><Trash2 size={13} /> Hapus {selected.length}</Button>
              )}
            </>)}
            <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
              {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
            </Button>
            {isSuperadmin && <Button onClick={openCreate}><Plus size={14} /> Tambah Server</Button>}
          </div>
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari nama/host…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 220 }} />
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 4 }}>
          {[['', 'Semua'], ['wireguard', 'WireGuard'], ['ipsec', 'IPsec']].map(([val, label]) => (
            <button key={val} onClick={() => { setTypeFilter(val); setPage(1); }}
              style={{ padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
                background: typeFilter === val ? 'rgba(59,130,246,0.15)' : 'transparent',
                color: typeFilter === val ? 'var(--accent-blue)' : 'var(--text-muted)',
                fontWeight: typeFilter === val ? 600 : 400, transition: 'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={servers} loading={loading} sort={sort} onSortChange={onSortChange}
          emptyMessage={onlyDeleted ? 'Tidak ada data terhapus' : 'Belum ada VPN server terdaftar'}
          selected={selected} onSelect={setSelected} />
        <div style={{ padding: '0 16px' }}><Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} /></div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit VPN Server' : 'Tambah VPN Server'} width={520}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Nama *" value={form.name} onChange={e => f('name', e.target.value)} placeholder="wg-jkt-1" disabled={!!editTarget} />
            <Select label="Type *" value={form.type} onChange={e => f('type', e.target.value)} disabled={!!editTarget}>
              <option value="wireguard">WireGuard</option>
              <option value="ipsec">IPsec (L2TP)</option>
            </Select>
          </div>
          <Input label="Description" value={form.description} onChange={e => f('description', e.target.value)} />

          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>Koneksi ke agent (internal)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <Input label="Host Agent *" value={form.host} onChange={e => f('host', e.target.value)} placeholder="10.0.0.5 / vpn-1.internal" />
            <Input label="Agent Port" type="number" value={form.agent_port} onChange={e => f('agent_port', +e.target.value)} />
          </div>
          <Input label={`Agent Token ${editTarget ? '(kosong = tidak berubah)' : '*'}`} type="password" value={form.agent_token} onChange={e => f('agent_token', e.target.value)} placeholder="samain dgn AGENT_TOKEN di container" />

          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>Info client (dilihat user)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Endpoint publik *" value={form.endpoint} onChange={e => f('endpoint', e.target.value)}
              placeholder={form.type === 'wireguard' ? 'vpn.example.com:51820' : 'vpn.example.com'} />
            <Input label="Pool *" value={form.pool} onChange={e => f('pool', e.target.value)}
              placeholder={form.type === 'wireguard' ? '10.8.0.0/24' : '192.168.42.10-192.168.42.19'} />
          </div>
          <Input label="Allowed IPs (routes client)" value={form.allowed_ips} onChange={e => f('allowed_ips', e.target.value)} />

          {form.type === 'wireguard' && (<>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="DNS" value={form.dns} onChange={e => f('dns', e.target.value)} placeholder="1.1.1.1" />
              <Input label="Keepalive (detik)" type="number" value={form.keepalive} onChange={e => f('keepalive', +e.target.value)} />
            </div>
            {editTarget && (
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Input label="Server Public Key" value={form.server_pubkey} onChange={e => f('server_pubkey', e.target.value)}
                      placeholder="klik 'Get Public Key' di sebelah, atau isi manual" />
                  </div>
                  <Button variant="secondary" onClick={handleSyncPubkey} loading={fetchingPubkey} style={{ marginBottom: 1 }}>
                    <RefreshCw size={13} /> Get Public Key
                  </Button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Ambil otomatis dari agent (butuh container wireguard sudah jalan). Tombol ini langsung menyimpan ke server.
                </p>
              </div>
            )}
          </>)}

          {form.type === 'ipsec' && (
            <Input label={`PSK ${editTarget ? '(kosong = tidak berubah)' : '*'}`} type="password" value={form.psk} onChange={e => f('psk', e.target.value)}
              placeholder="WAJIB sama dengan VPN_IPSEC_PSK container ipsec" />
          )}

          <Select label="Status" value={form.status} onChange={e => f('status', e.target.value)}>
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>{editTarget ? 'Simpan' : 'Tambah Server'}</Button>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus VPN Server" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus server <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.name}</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Server ini sudah berada di daftar terhapus, sehingga akan dihapus permanen.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={deletePermanent} onChange={e => setDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>

      {/* Bulk Delete */}
      <Modal open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} title="Hapus Bulk VPN Server" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Hapus <strong style={{ color: 'var(--accent-red)' }}>{selected.length} VPN server</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Server ini sudah berada di daftar terhapus, sehingga akan dihapus permanen.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={bulkDeletePermanent} onChange={e => setBulkDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setBulkDeleteConfirm(false)}>Batal</Button>
          <Button variant="danger" onClick={handleBulkDelete}>Hapus</Button>
        </div>
      </Modal>
    </div>
  );
}
